import { MathUtils } from 'three';
import type { Fencer } from './Fencer';
import type { BladeAction } from './types';

/** Reactive foil AI with distance management, feints, and priority awareness. */
export class FencingAI {
  private cooldown = 0;
  private aggressiveness: number;
  private skill: number;
  private tempo = 0;
  private preferHigh = true;

  constructor(aggressiveness = 0.55, skill = 0.65) {
    this.aggressiveness = aggressiveness;
    this.skill = skill;
  }

  setProfile(aggressiveness: number, skill: number): void {
    this.aggressiveness = aggressiveness;
    this.skill = skill;
  }

  update(dt: number, self: Fencer, foe: Fencer): void {
    this.cooldown = Math.max(0, this.cooldown - dt);
    this.tempo += dt;
    const dist = Math.abs(self.state.x - foe.state.x);
    const ideal = 1.75 + (1 - this.aggressiveness) * 0.55;

    let desiredX = self.state.x;
    let action: BladeAction | null = null;

    // Face the opponent
    self.state.facing = self.state.x < foe.state.x ? 1 : -1;

    // Blade aim toward foe
    self.state.bladeAngle = MathUtils.clamp((foe.state.x - self.state.x) * 0.05, -0.5, 0.5);

    // Defense — higher skill = better parry timing
    if (foe.isAttacking && dist < 2.35 && this.cooldown <= 0) {
      const window = foe.state.actionT / Math.max(0.01, foe.state.actionDur);
      const canSee = window > 0.12 && window < 0.75;
      if (canSee && Math.random() < this.skill * 0.9) {
        this.preferHigh = !this.preferHigh;
        action = this.preferHigh ? 'parryHigh' : 'parryLow';
        this.cooldown = 0.32;
      } else if (dist < 1.5 && Math.random() < 0.35) {
        // bail out
        desiredX = self.state.x - self.state.facing * 1.4;
        this.cooldown = 0.2;
      }
    }

    // Riposte after successful parry
    if (!action && self.state.canRiposte && this.cooldown <= 0) {
      if (Math.random() < this.skill * 0.95) {
        action = 'riposte';
        this.cooldown = 0.35;
      }
    }

    // Offense — attack on tempo
    if (!action && this.cooldown <= 0 && !foe.isAttacking && !foe.isParrying) {
      const attackChance = this.aggressiveness * dt * (dist < 2.2 && dist > 1.15 ? 3.2 : 0.6);
      if (Math.random() < attackChance) {
        // Feint retreat then lunge sometimes
        if (Math.random() < 0.15 && dist < 1.9) {
          desiredX = self.state.x - self.state.facing * 0.5;
          this.cooldown = 0.18;
        } else {
          action = Math.random() < 0.18 * this.aggressiveness ? 'fleche' : 'lunge';
          this.cooldown = 0.5 + Math.random() * 0.45;
        }
      } else if (dist < 1.1) {
        desiredX = self.state.x - self.state.facing * 1.0;
      }
    }

    // Distance management / balestra-like bounce
    if (!action) {
      const sway = Math.sin(this.tempo * 1.7) * 0.12 * self.state.facing;
      if (dist > ideal + 0.3) {
        desiredX = foe.state.x - self.state.facing * ideal + sway;
      } else if (dist < ideal - 0.4) {
        desiredX = self.state.x - self.state.facing * 1.15;
      } else {
        desiredX = self.state.x + sway;
      }
    }

    self.aiDrive(dt, desiredX, action);
  }
}
